<script>
  import { createEventDispatcher, onDestroy, onMount } from 'svelte';
  import { dndzone, SHADOW_ITEM_MARKER_PROPERTY_NAME } from 'svelte-dnd-action';
  import { DownloadCloud, GripVertical, ImageOff, Loader2, Plus, RefreshCw, Save, SlidersHorizontal, Trash2, Upload, X } from 'lucide-svelte';

  import ApiClient from '$lib/api';
  import { normalizeUrl } from '$lib/utils';

  const api = new ApiClient();
  const dispatch = createEventDispatcher();
  const DEFAULT_OPENMOJI_CODES = '1F600, 1F604, 1F606, 1F60D, 1F917, 1F44D, 1F44F, 1F64F, 1F389, 2728';

  let packs = [];
  let stickers = [];
  let selectedPack = null;
  let selectedFiles = [];
  let loading = false;
  let saving = false;
  let importing = false;
  let uploading = false;
  let showCreatePanel = false;
  let error = '';
  let notice = '';
  let openmojiInput = DEFAULT_OPENMOJI_CODES;
  let processingStickerId = null;
  let processingModal = null;
  let processingPreviewUrl = '';
  let processingPreviewBusy = false;
  let previewTimer = null;
  let resizeSize = 256;
  let removeBackground = true;
  let backgroundThreshold = 48;
  let brightness = 100;
  let contrast = 100;
  let saturation = 100;
  let processingImageKey = '';
  let processingImagePromise = null;
  let processingPreviewSeq = 0;
  let packSnapshot = '';
  let createSnapshot = '';

  let createForm = {
    name: '',
    icon: '',
    description: '',
    enabled: true,
    visible_from: '',
    visible_until: '',
    openmoji_codes: ''
  };

  let packForm = {
    name: '',
    icon: '',
    description: '',
    enabled: true,
    visible_from: '',
    visible_until: ''
  };

  $: selectedPackId = selectedPack?.id || null;
  $: packCount = packs.length;
  $: stickerCount = stickers.length;
  $: packDirty = !!selectedPackId && serializeDraft(normalizePackForm(packForm)) !== packSnapshot;
  $: createDirty = showCreatePanel && serializeDraft(normalizeCreateForm(createForm)) !== createSnapshot;
  $: hasPendingChanges = packDirty || createDirty;
  $: stateBusy = loading || saving || importing || uploading || !!processingStickerId || processingPreviewBusy;
  $: dispatch('statechange', {
    dirty: hasPendingChanges,
    busy: stateBusy,
    mode: showCreatePanel ? 'create' : selectedPackId ? 'pack' : 'idle'
  });

  function isImageAsset(value) {
    return /\.(png|jpe?g|gif|webp|bmp|svg|avif)$/i.test(String(value || '').split(/[?#]/, 1)[0]);
  }

  function toDateTimeLocal(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (part) => String(part).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  function toSchedulePayload(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }

  function packScheduleLabel(pack) {
    if (!pack?.visible_from && !pack?.visible_until) return 'Always visible';
    const from = pack.visible_from ? new Date(pack.visible_from).toLocaleString() : 'Now';
    const until = pack.visible_until ? new Date(pack.visible_until).toLocaleString() : 'No end';
    return `${from} - ${until}`;
  }

  function setMessage(type, value) {
    if (type === 'error') {
      error = value;
      notice = '';
      return;
    }
    notice = value;
    error = '';
  }

  function notifyStickerMessengerChanged() {
    if (typeof window === 'undefined') return;
    const version = String(Date.now());
    window.localStorage?.setItem('k2:stickers-version', version);
    window.dispatchEvent(new CustomEvent('k2:stickers-changed', { detail: { version } }));
  }

  function resetCreateForm() {
    createForm = {
      name: '',
      icon: '',
      description: '',
      enabled: true,
      visible_from: '',
      visible_until: '',
      openmoji_codes: ''
    };
  }

  function serializeDraft(value) {
    return JSON.stringify(value);
  }

  function normalizePackForm(form) {
    return {
      name: String(form?.name || '').trim(),
      icon: String(form?.icon || '').trim(),
      description: String(form?.description || '').trim(),
      enabled: Boolean(form?.enabled),
      visible_from: String(form?.visible_from || ''),
      visible_until: String(form?.visible_until || '')
    };
  }

  function normalizePackSource(pack) {
    return {
      name: String(pack?.name || '').trim(),
      icon: String(pack?.icon || '').trim(),
      description: String(pack?.description || '').trim(),
      enabled: Number(pack?.enabled ?? 1) === 1,
      visible_from: toDateTimeLocal(pack?.visible_from),
      visible_until: toDateTimeLocal(pack?.visible_until)
    };
  }

  function normalizeCreateForm(form) {
    return {
      name: String(form?.name || '').trim(),
      icon: String(form?.icon || '').trim(),
      description: String(form?.description || '').trim(),
      enabled: Boolean(form?.enabled),
      visible_from: String(form?.visible_from || ''),
      visible_until: String(form?.visible_until || ''),
      openmoji_codes: String(form?.openmoji_codes || '').trim()
    };
  }

  function syncPackSnapshot(pack = selectedPack) {
    packSnapshot = serializeDraft(normalizePackSource(pack));
  }

  function syncCreateSnapshot() {
    createSnapshot = serializeDraft(normalizeCreateForm(createForm));
  }

  function fillPackForm(pack) {
    packForm = normalizePackSource(pack);
    syncPackSnapshot(pack);
  }

  function discardMessage() {
    if (showCreatePanel) return 'Discard the new pack draft?';
    if (selectedPack?.name) return `Discard unsaved changes for "${selectedPack.name}"?`;
    return 'Discard unsaved sticker settings?';
  }

  function canLeaveCurrentView() {
    if (processingModal) {
      setMessage('error', 'Close the sticker image tools before leaving this view');
      return false;
    }

    if (!hasPendingChanges) return true;
    return window.confirm(discardMessage());
  }

  function openCreatePanel() {
    if (!canLeaveCurrentView()) return false;
    resetCreateForm();
    syncCreateSnapshot();
    showCreatePanel = true;
    selectedFiles = [];
    setMessage('notice', '');
    return true;
  }

  function closeCreatePanel(force = false) {
    if (!force && showCreatePanel && createDirty && !window.confirm(discardMessage())) {
      return false;
    }

    showCreatePanel = false;
    resetCreateForm();
    syncCreateSnapshot();
    return true;
  }

  async function loadPacks(preferredId = selectedPackId, options = {}) {
    const { preserveCreatePanel = showCreatePanel } = options;
    loading = true;
    try {
      packs = await api.get('/api/sticker-admin/packs');
      if (preserveCreatePanel) return;
      const nextPack = packs.find((pack) => String(pack.id) === String(preferredId)) || packs[0] || null;
      await selectPack(nextPack, false, { force: true });
    } catch (err) {
      setMessage('error', err.message || 'Load sticker packs failed');
    } finally {
      loading = false;
    }
  }

  async function loadStickers(packId = selectedPackId) {
    if (!packId) {
      stickers = [];
      return;
    }

    try {
      stickers = await api.get(`/api/sticker-admin/packs/${packId}/stickers`);
    } catch (err) {
      setMessage('error', err.message || 'Load stickers failed');
    }
  }

  async function selectPack(pack, loadItems = true, options = {}) {
    if (!options.force && !canLeaveCurrentView()) return false;

    showCreatePanel = false;
    selectedPack = pack;
    fillPackForm(pack);

    if (!pack) {
      stickers = [];
      return true;
    }

    if (loadItems) {
      await loadStickers(pack.id);
    } else if (pack.id) {
      await loadStickers(pack.id);
    }

    return true;
  }

  async function createPack() {
    if (!createForm.name.trim()) {
      setMessage('error', 'Pack name is required');
      return false;
    }

    saving = true;
    try {
      const result = await api.post('/api/sticker-admin/packs', {
        name: createForm.name,
        icon: createForm.icon,
        description: createForm.description,
        enabled: createForm.enabled ? 1 : 0,
        visible_from: toSchedulePayload(createForm.visible_from),
        visible_until: toSchedulePayload(createForm.visible_until),
        source: createForm.openmoji_codes.trim() ? 'openmoji' : 'manual',
        openmoji_codes: createForm.openmoji_codes
      });

      resetCreateForm();
      syncCreateSnapshot();
      showCreatePanel = false;
      await loadPacks(result?.pack?.id, { preserveCreatePanel: false });
      notifyStickerMessengerChanged();
      setMessage('notice', `Created pack${result?.imported?.length ? ` and imported ${result.imported.length} stickers` : ''}`);
      return true;
    } catch (err) {
      setMessage('error', err.message || 'Create sticker pack failed');
      return false;
    } finally {
      saving = false;
    }
  }

  async function savePack() {
    if (!selectedPackId) return true;
    if (!packForm.name.trim()) {
      setMessage('error', 'Pack name is required');
      return false;
    }

    saving = true;
    try {
      await api.put(`/api/sticker-admin/packs/${selectedPackId}`, {
        name: packForm.name,
        icon: packForm.icon,
        description: packForm.description,
        enabled: packForm.enabled ? 1 : 0,
        visible_from: toSchedulePayload(packForm.visible_from),
        visible_until: toSchedulePayload(packForm.visible_until),
        source: selectedPack?.source || 'manual',
        source_pack_id: selectedPack?.source_pack_id || ''
      });

      await loadPacks(selectedPackId, { preserveCreatePanel: false });
      notifyStickerMessengerChanged();
      setMessage('notice', 'Pack saved');
      return true;
    } catch (err) {
      setMessage('error', err.message || 'Save sticker pack failed');
      return false;
    } finally {
      saving = false;
    }
  }

  async function deletePack() {
    if (!selectedPackId) return;
    if (!window.confirm(`Delete sticker pack "${selectedPack.name}"?`)) return;

    saving = true;
    try {
      await api.delete(`/api/sticker-admin/packs/${selectedPackId}`);
      selectedPack = null;
      stickers = [];
      await loadPacks(null, { preserveCreatePanel: false });
      notifyStickerMessengerChanged();
      setMessage('notice', 'Pack deleted');
    } catch (err) {
      setMessage('error', err.message || 'Delete sticker pack failed');
    } finally {
      saving = false;
    }
  }

  async function importOpenMoji() {
    if (!selectedPackId) return;
    const codes = openmojiInput.trim() || DEFAULT_OPENMOJI_CODES;

    importing = true;
    try {
      const result = await api.post(`/api/sticker-admin/packs/${selectedPackId}/openmoji`, { codes });
      await loadPacks(selectedPackId);
      await loadStickers(selectedPackId);

      const imported = result?.imported?.length || 0;
      const failed = result?.errors?.length || 0;
      notifyStickerMessengerChanged();
      setMessage(failed ? 'error' : 'notice', `OpenMoji import: ${imported} imported${failed ? `, ${failed} failed` : ''}`);
    } catch (err) {
      setMessage('error', err.message || 'Import OpenMoji failed');
    } finally {
      importing = false;
    }
  }

  function handleFileInput(event) {
    selectedFiles = Array.from(event.currentTarget.files || []);
  }

  async function uploadFiles() {
    if (!selectedPackId || !selectedFiles.length) return;

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append('files', file, file.name));

    uploading = true;
    try {
      const result = await api.post(`/api/sticker-admin/packs/${selectedPackId}/upload`, formData);
      selectedFiles = [];
      await loadPacks(selectedPackId);
      await loadStickers(selectedPackId);
      notifyStickerMessengerChanged();
      setMessage('notice', `Uploaded ${result?.stickers?.length || 0} sticker files`);
    } catch (err) {
      setMessage('error', err.message || 'Upload sticker files failed');
    } finally {
      uploading = false;
    }
  }

  async function deleteSticker(sticker) {
    if (!window.confirm(`Delete sticker "${sticker.name || sticker.id}"?`)) return;

    try {
      await api.delete(`/api/sticker-admin/stickers/${sticker.id}`);
      await loadPacks(selectedPackId);
      await loadStickers(selectedPackId);
      notifyStickerMessengerChanged();
      setMessage('notice', 'Sticker deleted');
    } catch (err) {
      setMessage('error', err.message || 'Delete sticker failed');
    }
  }

  function handleStickerConsider(event) {
    stickers = event.detail.items;
  }

  async function handleStickerFinalize(event) {
    stickers = event.detail.items.filter((item) => !item[SHADOW_ITEM_MARKER_PROPERTY_NAME]);
    if (!selectedPackId) return;

    try {
      await api.post(`/api/sticker-admin/packs/${selectedPackId}/stickers/sort`, {
        orders: stickers.map((sticker, index) => ({
          id: sticker.id,
          sort_order: index
        }))
      });
      notifyStickerMessengerChanged();
      setMessage('notice', 'Sticker order saved');
    } catch (err) {
      setMessage('error', err.message || 'Save sticker order failed');
      await loadStickers(selectedPackId);
    }
  }

  function loadImageFromBlob(blob) {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(blob);
      const image = new Image();

      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Cannot read sticker image'));
      };
      image.src = objectUrl;
    });
  }

  function transparentDistance(pixel, sample) {
    const dr = pixel[0] - sample[0];
    const dg = pixel[1] - sample[1];
    const db = pixel[2] - sample[2];
    return Math.sqrt(dr * dr + dg * dg + db * db);
  }

  function clampNumber(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function getCornerSample(data, canvasWidth, canvasHeight, bounds) {
    const left = clampNumber(Math.round(bounds?.x ?? 0), 0, canvasWidth - 1);
    const top = clampNumber(Math.round(bounds?.y ?? 0), 0, canvasHeight - 1);
    const right = clampNumber(Math.round((bounds?.x ?? 0) + (bounds?.width ?? canvasWidth) - 1), 0, canvasWidth - 1);
    const bottom = clampNumber(Math.round((bounds?.y ?? 0) + (bounds?.height ?? canvasHeight) - 1), 0, canvasHeight - 1);
    const insetX = Math.min(3, Math.max(0, Math.floor((right - left) / 6)));
    const insetY = Math.min(3, Math.max(0, Math.floor((bottom - top) / 6)));
    const points = [
      [left + insetX, top + insetY],
      [right - insetX, top + insetY],
      [left + insetX, bottom - insetY],
      [right - insetX, bottom - insetY]
    ];

    const samples = [];
    for (const [x, y] of points) {
      const index = (y * canvasWidth + x) * 4;
      if (data[index + 3] === 0) continue;
      samples.push([data[index], data[index + 1], data[index + 2]]);
    }

    if (!samples.length) return null;
    const total = samples.reduce(
      (acc, pixel) => {
        acc[0] += pixel[0];
        acc[1] += pixel[1];
        acc[2] += pixel[2];
        return acc;
      },
      [0, 0, 0]
    );
    return total.map((value) => value / samples.length);
  }

  function applyBackgroundRemoval(canvas, threshold, bounds) {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const sample = getCornerSample(data, canvas.width, canvas.height, bounds);
    if (!sample) return;
    const feather = 18;

    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue;
      const distance = transparentDistance([data[i], data[i + 1], data[i + 2]], sample);
      if (distance <= threshold) {
        data[i + 3] = 0;
      } else if (distance <= threshold + feather) {
        data[i + 3] = Math.round(data[i + 3] * ((distance - threshold) / feather));
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }

  function revokeProcessingPreview() {
    if (processingPreviewUrl) {
      URL.revokeObjectURL(processingPreviewUrl);
      processingPreviewUrl = '';
    }
  }

  function openProcessingModal(sticker) {
    processingPreviewSeq += 1;
    processingModal = sticker;
    processingStickerId = null;
    processingImageKey = '';
    processingImagePromise = null;
    resizeSize = Math.max(64, Math.min(1024, Number(sticker.width) || 256));
    removeBackground = true;
    backgroundThreshold = 48;
    brightness = 100;
    contrast = 100;
    saturation = 100;
    scheduleProcessingPreview();
  }

  function closeProcessingModal() {
    processingPreviewSeq += 1;
    clearTimeout(previewTimer);
    previewTimer = null;
    processingModal = null;
    processingStickerId = null;
    processingPreviewBusy = false;
    revokeProcessingPreview();
  }

  function getProcessingImage(sticker) {
    const key = `${sticker.id}:${sticker.url}`;
    if (!processingImagePromise || processingImageKey !== key) {
      processingImageKey = key;
      processingImagePromise = fetch(normalizeUrl(sticker.url), { credentials: 'include' })
        .then((response) => {
          if (!response.ok) throw new Error(`Cannot load sticker image: HTTP ${response.status}`);
          return response.blob();
        })
        .then((blob) => loadImageFromBlob(blob));
    }
    return processingImagePromise;
  }

  function scheduleProcessingPreview() {
    if (!processingModal) return;
    clearTimeout(previewTimer);
    previewTimer = setTimeout(() => {
      updateProcessingPreview();
    }, 160);
  }

  async function processStickerImage(sticker) {
    const size = Math.max(64, Math.min(1024, Number(resizeSize) || 256));
    const image = await getProcessingImage(sticker);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d', { alpha: true });
    ctx.clearRect(0, 0, size, size);
    ctx.filter = `brightness(${Number(brightness) || 100}%) contrast(${Number(contrast) || 100}%) saturate(${Number(saturation) || 100}%)`;

    const scale = Math.min(size / image.naturalWidth, size / image.naturalHeight);
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const x = Math.round((size - width) / 2);
    const y = Math.round((size - height) / 2);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, x, y, width, height);

    if (removeBackground) {
      applyBackgroundRemoval(
        canvas,
        Math.max(0, Math.min(255, Number(backgroundThreshold) || 0)),
        { x, y, width, height }
      );
    }

    const processedBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!processedBlob) throw new Error('Cannot export processed sticker');

    return {
      blob: processedBlob,
      width: size,
      height: size
    };
  }

  async function updateProcessingPreview() {
    if (!processingModal) return;

    const seq = ++processingPreviewSeq;
    const sticker = processingModal;
    processingPreviewBusy = true;
    try {
      const processed = await processStickerImage(sticker);
      if (seq !== processingPreviewSeq || processingModal !== sticker) return;
      revokeProcessingPreview();
      processingPreviewUrl = URL.createObjectURL(processed.blob);
    } catch (err) {
      if (seq === processingPreviewSeq) {
        setMessage('error', err.message || 'Preview sticker failed');
      }
    } finally {
      if (seq === processingPreviewSeq) {
        processingPreviewBusy = false;
      }
    }
  }

  async function applyProcessingModal() {
    if (!selectedPackId || !processingModal || processingStickerId) return;

    const sticker = processingModal;
    processingStickerId = sticker.id;
    try {
      const processed = await processStickerImage(sticker);
      const formData = new FormData();
      formData.append('file', processed.blob, `${sticker.name || sticker.id}-processed.png`);
      formData.append('width', String(processed.width));
      formData.append('height', String(processed.height));

      await api.post(`/api/sticker-admin/stickers/${sticker.id}/replace`, formData);
      await loadPacks(selectedPackId);
      await loadStickers(selectedPackId);
      notifyStickerMessengerChanged();
      setMessage('notice', 'Sticker processed');
      closeProcessingModal();
    } catch (err) {
      setMessage('error', err.message || 'Process sticker failed');
    } finally {
      processingStickerId = null;
    }
  }

  $: if (processingModal) {
    resizeSize;
    removeBackground;
    backgroundThreshold;
    brightness;
    contrast;
    saturation;
    scheduleProcessingPreview();
  }

  onDestroy(() => {
    clearTimeout(previewTimer);
    revokeProcessingPreview();
  });

  onMount(() => {
    loadPacks();
    syncCreateSnapshot();
  });

  export async function commitChanges() {
    if (processingModal) {
      setMessage('error', 'Apply or close the sticker image tools before saving settings');
      return false;
    }

    if (showCreatePanel) {
      if (!createDirty) return true;
      return await createPack();
    }

    if (selectedPackId && packDirty) {
      return await savePack();
    }

    return true;
  }

  export function canCloseModal() {
    return canLeaveCurrentView();
  }

  export function canNavigateAway() {
    return canLeaveCurrentView();
  }
</script>

<section class="flex min-h-full flex-col gap-4 p-4 sm:p-6">
  <div class="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
    <aside class="min-h-0 rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div class="border-b border-slate-200 px-4 py-4">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h2 class="text-base font-semibold text-slate-900">Sticker packs</h2>
            <p class="text-sm text-slate-500">{packCount} packs</p>
          </div>
          <button
            type="button"
            class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
            on:click={() => loadPacks(showCreatePanel ? null : selectedPackId, { preserveCreatePanel: showCreatePanel })}
            disabled={loading}
            title="Refresh">
            {#if loading}
              <Loader2 size={16} class="animate-spin" />
            {:else}
              <RefreshCw size={16} />
            {/if}
          </button>
        </div>

        <button
          type="button"
          class="mt-4 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          on:click={openCreatePanel}
          disabled={saving || importing || uploading}>
          <Plus size={16} />
          Create pack
        </button>
      </div>

      <div class="max-h-[34rem] space-y-2 overflow-y-auto p-3">
        {#if packs.length}
          {#each packs as pack (pack.id)}
            <button
              type="button"
              class="flex w-full min-w-0 items-center gap-3 rounded-2xl border px-3 py-3 text-left transition {showCreatePanel
                ? 'border-slate-200 hover:bg-slate-50'
                : selectedPackId === pack.id
                  ? 'border-blue-300 bg-blue-50 shadow-sm'
                  : 'border-slate-200 hover:bg-slate-50'}"
              on:click={() => selectPack(pack)}>
              <span class="flex h-12 min-w-[3rem] w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-100 text-xl text-slate-700">
                {#if isImageAsset(pack.icon)}
                  <img src={normalizeUrl(pack.icon)} alt={pack.name} class="h-full w-full object-contain p-1.5" />
                {:else}
                  {pack.icon || 'S'}
                {/if}
              </span>
              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm font-semibold text-slate-900">{pack.name}</span>
                <span class="block truncate text-xs text-slate-500">{pack.sticker_count || 0} stickers · {pack.enabled ? 'Enabled' : 'Disabled'}</span>
                <span class="block truncate text-[11px] text-slate-400">{packScheduleLabel(pack)}</span>
              </span>
            </button>
          {/each}
        {:else}
          <div class="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
            No sticker packs
          </div>
        {/if}
      </div>
    </aside>

    <div class="min-w-0 space-y-4">
      {#if showCreatePanel}
        <section class="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div class="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-start sm:justify-between">
            <div class="min-w-0">
              <h2 class="text-lg font-semibold text-slate-900">Create pack</h2>
              <p class="text-sm text-slate-500">Create a new sticker pack or seed it from OpenMoji.</p>
            </div>
            <button
              type="button"
              class="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50"
              on:click={() => closeCreatePanel()}>
              Cancel
            </button>
          </div>

          <div class="grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div class="space-y-4">
              <label class="block space-y-1">
                <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">Name</span>
                <input
                  bind:value={createForm.name}
                  class="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  placeholder="OpenMoji reactions" />
              </label>
              <label class="block space-y-1">
                <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">Icon</span>
                <input
                  bind:value={createForm.icon}
                  class="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  placeholder="Emoji or image URL" />
              </label>
              <label class="block space-y-1">
                <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">Description</span>
                <textarea
                  bind:value={createForm.description}
                  rows="4"
                  class="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"></textarea>
              </label>
              <div class="grid gap-3 sm:grid-cols-2">
                <label class="block space-y-1">
                  <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">Visible from</span>
                  <input
                    bind:value={createForm.visible_from}
                    type="datetime-local"
                    class="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                </label>
                <label class="block space-y-1">
                  <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">Visible until</span>
                  <input
                    bind:value={createForm.visible_until}
                    type="datetime-local"
                    class="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                </label>
              </div>
              <label class="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" bind:checked={createForm.enabled} class="h-4 w-4 rounded border-slate-300" />
                Enabled in messenger
              </label>
            </div>

            <div class="space-y-4">
              <label class="block space-y-1">
                <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">OpenMoji seed</span>
                <textarea
                  bind:value={createForm.openmoji_codes}
                  rows="8"
                  class="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  placeholder={DEFAULT_OPENMOJI_CODES}></textarea>
              </label>
              <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                Use the outer `OK` button to save this draft, or press `Create now` if you want to create the pack immediately.
              </div>
              <button
                type="button"
                class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                on:click={createPack}
                disabled={saving}>
                {#if saving}
                  <Loader2 size={16} class="animate-spin" />
                {:else}
                  <Plus size={16} />
                {/if}
                Create now
              </button>
            </div>
          </div>
        </section>
      {:else if selectedPack}
        <section class="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div class="flex flex-col gap-4 border-b border-slate-200 px-4 py-4 lg:flex-row lg:items-start lg:justify-between">
            <div class="flex min-w-0 items-center gap-4">
              <span class="flex h-16 min-w-[4rem] w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-100 text-2xl text-slate-700">
                {#if isImageAsset(packForm.icon)}
                  <img src={normalizeUrl(packForm.icon)} alt={packForm.name || selectedPack.name} class="h-full w-full object-contain p-2" />
                {:else}
                  {packForm.icon || selectedPack.icon || 'S'}
                {/if}
              </span>
              <div class="min-w-0">
                <h2 class="truncate text-lg font-semibold text-slate-900">{selectedPack?.name || 'Sticker pack'}</h2>
                <p class="text-sm text-slate-500">{stickerCount} stickers in this pack</p>
                <p class="truncate text-xs text-slate-400">{packScheduleLabel(selectedPack)}</p>
              </div>
            </div>

            <div class="flex flex-wrap items-center gap-2">
              <button
                type="button"
                class="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-red-200 px-4 text-sm font-medium text-red-600 hover:bg-red-50"
                on:click={deletePack}
                disabled={saving}>
                <Trash2 size={15} />
                Delete
              </button>
              <button
                type="button"
                class="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                on:click={savePack}
                disabled={saving || !packDirty}>
                {#if saving}
                  <Loader2 size={16} class="animate-spin" />
                {:else}
                  <Save size={16} />
                {/if}
                Save now
              </button>
            </div>
          </div>

          <div class="grid gap-4 border-b border-slate-200 p-4 lg:grid-cols-2">
            <label class="block space-y-1">
              <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">Name</span>
              <input
                bind:value={packForm.name}
                class="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
            </label>
            <label class="block space-y-1">
              <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">Icon or image URL</span>
              <input
                bind:value={packForm.icon}
                class="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
            </label>
            <label class="block space-y-1 lg:col-span-2">
              <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">Description</span>
              <textarea
                bind:value={packForm.description}
                rows="3"
                class="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"></textarea>
            </label>
            <label class="block space-y-1">
              <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">Visible from</span>
              <input
                bind:value={packForm.visible_from}
                type="datetime-local"
                class="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
            </label>
            <label class="block space-y-1">
              <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">Visible until</span>
              <input
                bind:value={packForm.visible_until}
                type="datetime-local"
                class="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
            </label>
            <label class="inline-flex items-center gap-2 text-sm text-slate-700 lg:col-span-2">
              <input type="checkbox" bind:checked={packForm.enabled} class="h-4 w-4 rounded border-slate-300" />
              Enabled in messenger
            </label>
          </div>

          <div class="grid gap-4 border-b border-slate-200 p-4 xl:grid-cols-[minmax(0,1fr)_minmax(320px,360px)]">
            <div class="space-y-3">
              <div class="flex flex-wrap items-center justify-between gap-3">
                <label for="openmoji-codes" class="text-xs font-semibold uppercase tracking-wide text-slate-500">OpenMoji codes</label>
                <button
                  type="button"
                  class="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border border-slate-200 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  on:click={importOpenMoji}
                  disabled={importing}>
                  {#if importing}
                    <Loader2 size={15} class="animate-spin" />
                  {:else}
                    <DownloadCloud size={15} />
                  {/if}
                  Import
                </button>
              </div>
              <textarea
                id="openmoji-codes"
                bind:value={openmojiInput}
                rows="7"
                class="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"></textarea>
            </div>

            <div class="space-y-3">
              <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">Upload sticker files</span>
              <div class="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
                <input
                  type="file"
                  multiple
                  accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,image/avif"
                  class="block w-full text-sm text-slate-600"
                  on:change={handleFileInput} />
                <div class="mt-4 flex items-center justify-between gap-3">
                  <span class="min-w-0 truncate text-xs text-slate-500">{selectedFiles.length} selected</span>
                  <button
                    type="button"
                    class="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                    on:click={uploadFiles}
                    disabled={!selectedFiles.length || uploading}>
                    {#if uploading}
                      <Loader2 size={15} class="animate-spin" />
                    {:else}
                      <Upload size={15} />
                    {/if}
                    Upload
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div class="flex items-center justify-between border-b border-slate-200 px-4 py-4">
            <div>
              <h3 class="text-base font-semibold text-slate-900">Stickers</h3>
              <p class="text-sm text-slate-500">Drag to reorder. Click the tool icon to resize or remove background.</p>
            </div>
          </div>

          <div
            use:dndzone={{
              items: stickers,
              flipDurationMs: 120,
              dragHandleSelectors: ['.sticker-drag-handle'],
              morphDisabled: true,
              dropTargetClasses: ['sticker-dnd-active']
            }}
            on:consider={handleStickerConsider}
            on:finalize={handleStickerFinalize}
            class="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {#each stickers as sticker (`${sticker.id}-${sticker[SHADOW_ITEM_MARKER_PROPERTY_NAME] ? 'shadow' : 'item'}`)}
              {#if sticker[SHADOW_ITEM_MARKER_PROPERTY_NAME]}
                <div
                  class="flex min-h-[190px] items-center justify-center rounded-2xl border-2 border-dashed border-blue-300 bg-blue-50/70 text-xs font-semibold text-blue-600"
                  data-is-dnd-shadow-item-hint={sticker[SHADOW_ITEM_MARKER_PROPERTY_NAME]}>
                  Drop here
                </div>
              {:else}
                <div class="group relative rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                  <button
                    type="button"
                    class="sticker-drag-handle absolute left-2 top-2 inline-flex h-8 w-8 cursor-grab items-center justify-center rounded-lg bg-white text-slate-500 opacity-100 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50 active:cursor-grabbing sm:opacity-0 sm:group-hover:opacity-100"
                    title="Drag to sort">
                    <GripVertical size={14} />
                  </button>
                  <div class="flex aspect-square items-center justify-center rounded-xl bg-slate-50">
                    <img src={normalizeUrl(sticker.url)} alt={sticker.name} class="h-full w-full object-contain p-2" loading="lazy" />
                  </div>
                  <div class="mt-2 min-w-0">
                    <p class="truncate text-xs font-semibold text-slate-800">{sticker.name || sticker.id}</p>
                    <p class="truncate text-[11px] text-slate-500">{sticker.source || 'manual'}</p>
                  </div>
                  <div class="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      on:click={() => openProcessingModal(sticker)}
                      title="Resize and effects"
                      aria-label="Resize and effects">
                      {#if processingStickerId === sticker.id}
                        <Loader2 size={13} class="animate-spin" />
                      {:else}
                        <SlidersHorizontal size={15} />
                      {/if}
                    </button>
                    <button
                      type="button"
                      class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                      on:click={() => deleteSticker(sticker)}
                      title="Delete sticker"
                      aria-label="Delete sticker">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              {/if}
            {/each}
          </div>
        </section>
      {:else}
        <section class="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center shadow-sm">
          <h2 class="text-lg font-semibold text-slate-900">Select a sticker pack</h2>
          <p class="mt-2 text-sm text-slate-500">Choose a pack from the left, or create a new pack to start publishing stickers.</p>
        </section>
      {/if}
    </div>
  </div>

  {#if error}
    <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{error}</div>
  {:else if notice}
    <div class="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">{notice}</div>
  {/if}

  {#if processingModal}
    <div class="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/50 p-0 sm:items-center sm:p-6">
      <div class="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:rounded-2xl">
        <div class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div class="min-w-0">
            <h3 class="truncate text-base font-semibold text-slate-900">Sticker image tools</h3>
            <p class="truncate text-xs text-slate-500">{processingModal.name || processingModal.id}</p>
          </div>
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
            on:click={closeProcessingModal}
            aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div class="grid min-h-0 flex-1 gap-0 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_360px]">
          <div class="flex min-h-[320px] items-center justify-center bg-slate-100 p-4">
            <div class="checkerboard flex aspect-square w-full max-w-[520px] items-center justify-center rounded-xl border border-slate-300">
              {#if processingPreviewBusy && !processingPreviewUrl}
                <Loader2 size={28} class="animate-spin text-slate-500" />
              {:else if processingPreviewUrl}
                <img src={processingPreviewUrl} alt="Processed preview" class="h-full w-full object-contain p-4" />
              {:else}
                <img src={normalizeUrl(processingModal.url)} alt={processingModal.name} class="h-full w-full object-contain p-4" />
              {/if}
            </div>
          </div>

          <div class="space-y-4 border-t border-slate-200 p-4 lg:border-l lg:border-t-0">
            <label class="space-y-1">
              <span class="text-xs font-semibold uppercase text-slate-500">Canvas size</span>
              <input
                bind:value={resizeSize}
                type="number"
                min="64"
                max="1024"
                step="16"
                class="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
            </label>

            <label class="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700">
              <span>Transparent background</span>
              <input type="checkbox" bind:checked={removeBackground} class="h-4 w-4 rounded border-slate-300" />
            </label>

            <label class="space-y-1">
              <span class="text-xs font-semibold uppercase text-slate-500">Background threshold: {backgroundThreshold}</span>
              <input bind:value={backgroundThreshold} type="range" min="0" max="140" class="w-full" disabled={!removeBackground} />
            </label>

            <label class="space-y-1">
              <span class="text-xs font-semibold uppercase text-slate-500">Brightness: {brightness}%</span>
              <input bind:value={brightness} type="range" min="40" max="180" class="w-full" />
            </label>

            <label class="space-y-1">
              <span class="text-xs font-semibold uppercase text-slate-500">Contrast: {contrast}%</span>
              <input bind:value={contrast} type="range" min="40" max="180" class="w-full" />
            </label>

            <label class="space-y-1">
              <span class="text-xs font-semibold uppercase text-slate-500">Saturation: {saturation}%</span>
              <input bind:value={saturation} type="range" min="0" max="220" class="w-full" />
            </label>

            <div class="rounded-lg bg-slate-50 p-3 text-xs leading-5 text-slate-600">
              Resize keeps the original ratio and centers the sticker on a transparent square. Background removal samples the image corners, then makes pixels close to that background color transparent.
            </div>
          </div>
        </div>

        <div class="flex flex-col-reverse gap-2 border-t border-slate-200 p-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            class="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            on:click={closeProcessingModal}>
            Cancel
          </button>
          <button
            type="button"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            on:click={applyProcessingModal}
            disabled={!!processingStickerId || processingPreviewBusy}>
            {#if processingStickerId}
              <Loader2 size={16} class="animate-spin" />
            {:else}
              <ImageOff size={16} />
            {/if}
            Apply to server
          </button>
        </div>
      </div>
    </div>
  {/if}
</section>

<style>
  .checkerboard {
    background-color: #fff;
    background-image:
      linear-gradient(45deg, #e2e8f0 25%, transparent 25%),
      linear-gradient(-45deg, #e2e8f0 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #e2e8f0 75%),
      linear-gradient(-45deg, transparent 75%, #e2e8f0 75%);
    background-position:
      0 0,
      0 10px,
      10px -10px,
      -10px 0;
    background-size: 20px 20px;
  }

  :global(.sticker-dnd-active) {
    outline: 2px solid rgba(37, 99, 235, 0.28);
    outline-offset: 2px;
    border-radius: 12px;
  }
</style>
